import type { CriteriaLabel, TemplateScoringCriteria } from '@piccolohealth/pbs-common';
import { ChakraV3, DndKitCore, DndKitSortable, DragHandle, Sortable } from '@piccolohealth/ui';
import { P, uuid } from '@piccolohealth/util';
import React from 'react';
import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { HookedCriteriaLabelsControl } from './HookedCriteriaLabelsControl';
import type { FormValues } from './TemplateScoringChecklistEditor';

interface TemplateScoringCriteriaItemProps {
  categoryIndex: number;
  criteria: TemplateScoringCriteria;
  index: number;
  onRemove: () => void;
  availableLabels: CriteriaLabel[];
  onUpdateCriteriaLabels: (labels: CriteriaLabel[]) => void;
}

const TemplateScoringCriteriaItem = (props: TemplateScoringCriteriaItemProps) => {
  const { criteria, categoryIndex, index, onRemove, availableLabels, onUpdateCriteriaLabels } =
    props;

  const {
    field: { value, onChange },
  } = useController({
    name: `categories.${categoryIndex}.criterias.${index}.description`,
  });

  const [inputValue, setInputValue] = React.useState(value);
  const ref = React.useRef(null);

  const { attributes, setNodeRef, listeners, transform, transition } = DndKitSortable.useSortable({
    id: criteria.id,
  });

  const onEdit = React.useCallback(
    (v: string) => {
      onChange(v);
    },
    [onChange],
  );

  return (
    <Sortable ref={setNodeRef} transform={transform} transition={transition}>
      <ChakraV3.Stack
        w='full'
        px={2}
        py={1}
        gap={2}
        bg='white'
        _hover={{ bg: 'gray.50' }}
        rounded='lg'
      >
        <ChakraV3.Editable.Root
          value={inputValue}
          onValueChange={({ value }) => setInputValue(value)}
          onValueCommit={({ value }) => onEdit(value)}
          w='full'
          activationMode='none'
          className='group'
        >
          <ChakraV3.HStack ref={ref} w='full' gap={0} align='baseline'>
            <DragHandle listeners={listeners} attributes={attributes} size='sm' />
            <ChakraV3.HStack gap={1} w='full'>
              <ChakraV3.Text flexShrink={0} fontSize='sm'>
                {categoryIndex + 1}.{index + 1} -
              </ChakraV3.Text>
              <ChakraV3.Editable.Preview
                w='full'
                cursor='pointer'
                textAlign='left'
                p={0}
                rounded='none'
                lineHeight='normal'
                fontSize='sm'
              />
              <ChakraV3.Editable.Input
                as={ChakraV3.Textarea}
                overflow='hidden'
                resize='none'
                lineHeight='normal'
                border='none'
                textAlign='left'
                minH={0}
                p={0}
                fontSize='sm'
                rounded='none'
                h='auto'
                {...{ minRows: 1, rows: 1, autoresize: true }}
              />
            </ChakraV3.HStack>
            <ChakraV3.Spacer />
            <ChakraV3.Stack w='fit-content' maxW='180px' flexShrink={0}>
              <HookedCriteriaLabelsControl
                name={`categories.${categoryIndex}.criterias.${index}.labelIds`}
                availableLabels={availableLabels}
                onUpdateCriteriaLabels={onUpdateCriteriaLabels}
                size='sm'
                variant='selectGhost'
                showArrow={false}
              />
            </ChakraV3.Stack>
            <ChakraV3.EditableControls onRemove={onRemove} />
          </ChakraV3.HStack>
        </ChakraV3.Editable.Root>
      </ChakraV3.Stack>
    </Sortable>
  );
};

interface Props {
  categoryIndex: number;
}

export const TemplateScoringCriterias = (props: Props) => {
  const { categoryIndex } = props;
  const { watch } = useFormContext<FormValues>();

  const { fields, append, remove, move } = useFieldArray<
    FormValues,
    `categories.${number}.criterias`
  >({
    name: `categories.${categoryIndex}.criterias`,
  });

  const { field: criteriaLabelsField } = useController({ name: 'criteriaLabels' });

  const criterias = fields as unknown as TemplateScoringCriteria[];

  const watchedData = watch();

  const availableLabels = React.useMemo(() => {
    return watchedData.criteriaLabels || [];
  }, [watchedData]);

  const onUpdateCriteriaLabels = React.useCallback(
    (labels: CriteriaLabel[]) => {
      criteriaLabelsField.onChange(labels);
    },
    [criteriaLabelsField],
  );

  const sensors = DndKitCore.useSensors(
    DndKitCore.useSensor(DndKitCore.PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const onDragEnd = React.useCallback(
    async (event: DndKitCore.DragEndEvent) => {
      const { active, over } = event;

      if (!active || !over) {
        return;
      }

      const oldIndex = criterias.findIndex((item) => item.id === active.id);
      const newIndex = criterias.findIndex((item) => item.id === over.id);

      if (oldIndex === newIndex) {
        return;
      }

      move(oldIndex, newIndex);
    },
    [criterias, move],
  );

  const onAdd = React.useCallback(() => {
    append({
      id: uuid(),
      description: 'Example criteria',
      labelIds: [],
    });
  }, [append]);

  return (
    <ChakraV3.Stack gap={0}>
      <DndKitCore.DndContext
        sensors={sensors}
        collisionDetection={DndKitCore.closestCenter}
        onDragEnd={onDragEnd}
      >
        {P.isEmpty(criterias) && (
          <ChakraV3.Text fontSize='sm' color='secondary'>
            No criteria added yet
          </ChakraV3.Text>
        )}
        <DndKitSortable.SortableContext
          strategy={DndKitSortable.verticalListSortingStrategy}
          items={criterias.map(({ id }) => id)}
        >
          {criterias.map((criteria, index) => (
            <TemplateScoringCriteriaItem
              key={criteria.id}
              categoryIndex={categoryIndex}
              index={index}
              criteria={criteria}
              onRemove={() => remove(index)}
              availableLabels={availableLabels}
              onUpdateCriteriaLabels={onUpdateCriteriaLabels}
            />
          ))}
        </DndKitSortable.SortableContext>
      </DndKitCore.DndContext>

      <ChakraV3.Button
        onClick={onAdd}
        size='sm'
        variant='outline'
        borderWidth='2px'
        borderStyle='dashed'
        color='secondary'
        mt={2}
      >
        <FaPlus />
        Add criteria
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};
